import { useCallback, useEffect, useState } from 'react';
import { creationRequestsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  DEFAULT_PHONE_PREFIX,
  indianMobileDigits,
} from '../domain/userCreationRequest/onboardingFormOptions';

const publicRequest = { skipAuth: true };

function emptyForm() {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    objective: '',
    selectedObjectives: [],
  };
}

export default function useStudentAccessRequest() {
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const loadSuggestions = useCallback(async () => {
    try {
      const res = await creationRequestsAPI.objectiveSuggestions(publicRequest);
      setSuggestions(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      if (err.response?.data?.code === 'FEATURE_DISABLED') {
        setDisabled(true);
        setError('Account requests are not available right now.');
      }
    }
  }, []);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const onChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      if (name === 'phone') return { ...prev, phone: indianMobileDigits(value) };
      return { ...prev, [name]: value };
    });
  }, []);

  const onPickSuggestion = useCallback((label) => {
    setFormData((prev) => {
      const selected = prev.selectedObjectives.includes(label)
        ? prev.selectedObjectives.filter((item) => item !== label)
        : [...prev.selectedObjectives, label];
      return { ...prev, selectedObjectives: selected };
    });
  }, []);

  const submit = useCallback(
    async (event) => {
      event?.preventDefault?.();
      setSubmitting(true);
      setError('');
      setSuccess('');
      try {
        await creationRequestsAPI.submit(
          {
            requestKind: 'student-access',
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: `${DEFAULT_PHONE_PREFIX}${formData.phone}`,
            objectives: formData.selectedObjectives,
            objectiveNote: formData.objective,
            objective: formData.objective,
          },
          publicRequest
        );
        setSuccess('Request submitted. Our team will review it and create your account if it is approved.');
        setFormData(emptyForm());
        loadSuggestions();
      } catch (err) {
        if (err.response?.data?.code === 'FEATURE_DISABLED') {
          setDisabled(true);
          setError('Account requests are not available right now.');
        } else if (err.response?.status === 429) {
          setError(getApiErrorMessage(err, 'Too many account requests. Please try again later.'));
        } else {
          setError(getApiErrorMessage(err, 'Could not submit your request'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [formData, loadSuggestions]
  );

  return {
    formData,
    suggestions,
    submitting,
    error,
    success,
    disabled,
    onChange,
    onPickSuggestion,
    submit,
  };
}
